/**
 * Wires a Vue Router instance so e2e/route-coverage can report "what
 * fraction of the app's routes did this run actually visit" — independent
 * of nyc's Istanbul line coverage (see ./coverage.ts).
 *
 * Deliberately reads the router's OWN resolved route table and match
 * results (`getRoutes()` / `afterEach`) instead of hand-parsing `pages/**`
 * file names, so route groups, dynamic segments, and nested layouts resolve
 * exactly the way the framework resolves them.
 *
 * Exported from a dedicated `@repo/e2e/routeTracking` entry point (not the
 * package's main index) because this is the one piece of `@repo/e2e` that
 * ships in a production browser bundle (called from an app's Nuxt plugin or
 * router setup) — the main index also re-exports Node-only helpers
 * (coverage.ts uses `node:fs`/`node:path`) that must never be pulled into
 * client code.
 *
 * Typed structurally against vue-router's `Router` rather than importing
 * `vue-router` itself, so this package takes on no new dependency and stays
 * usable from any app regardless of its vue-router/Nuxt version.
 *
 * Callers are responsible for only invoking this client-side (Nuxt plugins
 * here gate on `import.meta.client`; account's router module only ever runs
 * in the browser, since account has no SSR entry) — there is deliberately
 * no `typeof window` guard here, since this package's tsconfig has no DOM
 * lib and detecting "no window" without referencing `window` by name isn't
 * worth the contortion for a function nothing calls outside the browser.
 */
interface RouteTrackableRouter {
  getRoutes(): { path: string }[]
  afterEach(guard: (to: { matched: { path: string }[] }) => void): void
}

export function installRouteTracking(router: RouteTrackableRouter): void {
  const win = globalThis as unknown as { __ROUTES__?: string[]; __VISITED_ROUTES__?: string[] }
  win.__ROUTES__ = router.getRoutes().map((route) => route.path)
  win.__VISITED_ROUTES__ = []

  router.afterEach((to) => {
    const matched = to.matched.at(-1)
    if (matched && !win.__VISITED_ROUTES__!.includes(matched.path)) {
      win.__VISITED_ROUTES__!.push(matched.path)
    }
  })
}
